import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { LetDirective } from '@directives/app-let.directive';
import { NullableString } from '@models/app.models';
import { CalendarDateCssClass } from '@models/requests/route-schedule-request.model';
import { Store } from '@ngrx/store';
import { RequiredFieldPipe } from '@pipes/required-field/required-field.pipe';
import { selectIsMobile } from '@selectors/app.selector';
import { selectAppCmsCopy } from '@selectors/cms.selector';
import { createDateFromDateLike, stringAsBooleanProp } from '@utils/data-utils';
import { DateTime } from 'luxon';
import {
  BsDatepickerDirective,
  BsDatepickerModule, BsDaterangepickerDirective, BsLocaleService,
} from 'ngx-bootstrap/datepicker';
import { InputErrorsComponent } from '../input-errors/input-errors.component';
import {DEFAULT_MIN_DATE} from "@constants/home.constants";

/**
 * Date picker component that swaps between mobile and web versions
 * based on the current platform.
 *
 * For mobile, the ion datepicker is used inside a popover, the value
 * of the input is set to the actual formatted string that is selected
 * and that is what is displayed to the user. This input is readonly
 * and serves only as a display of the value and a button to trigger
 * the popover.
 *
 * For web, the bootstrap datepicker is used, which requires parsing the
 * format string from the string expected for moment.js/luxor, to the
 * non-standard format expected by bootstrap.
 */


@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [
    RequiredFieldPipe,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    BsDatepickerModule,
    InputErrorsComponent,
    LetDirective,
  ],
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatePickerComponent implements OnInit {
  constructor(private store: Store, private localeService: BsLocaleService) {
  }

  // inputs
  @Input() label: NullableString;
  @Input() name: string;
  @Input() minDate: Date | NullableString = DEFAULT_MIN_DATE;
  @Input() maxDate: Date | NullableString;
  @Input() datesEnabled: Date[] | undefined;
  @Input() dateClasses: CalendarDateCssClass[] | undefined;
  @Input() displayMonths: number = 1;
  @Input() rangePicker: boolean = false;
  @Input() theme: string = 'theme-dark-blue';

  /**
   * Group can be a number in the case that the group is an
   * index into a form array.
   */
  @Input() group: string | number | null;
  @Input() control: AbstractControl | UntypedFormControl | null | undefined;
  @Input() format: string;
  @Input() extraErrors?: { [key: string]: string };
  @Input() suppressErrors?: string[];
  @Input() isAnimated: boolean;
  @Input() adaptivePosition: boolean;
  @Input() placement: 'top' | 'bottom' | 'left' | 'right' = 'top';
  @Input() extraClasses?: string;
  @Input() inputClass?: string;

  // two-way
  @Input() value: NullableString;
  @Output() valueChange = new EventEmitter<NullableString>();
  @Output() rangeValueChange = new EventEmitter<(Date | undefined)[]>();

  appCopy$ = this.store.select(selectAppCmsCopy);
  isMobile$ = this.store.select(selectIsMobile);

  @ViewChild('datePicker') datePicker: BsDatepickerDirective;
  @ViewChild('dateRangePicker') dateRangePicker: BsDaterangepickerDirective;

  toggle(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.rangePicker) {
      this.dateRangePicker.toggle();
    } else {
      this.datePicker.toggle();
    }
  }

  /**
   * Bootstrap date picker date format does not match those used
   * by other date libraries, the d for days and y for years
   * must be upper-cased to achieve the same formatting.
   */
  get bootstrapFormat(): string {
    return this.format.replace(/d/g, 'D').replace(/y/g, 'Y');
  }

  // getters
  get formControl(): UntypedFormControl | null | undefined {
    return this.control as UntypedFormControl;
  }

  /**
   * bootstrap date picker requires the minimum date as
   * as a JS date.
   */
  get minDateAsDate(): Date | undefined {
    return createDateFromDateLike(this.minDate, this.format);
  }

  /**
   * bootstrap date picker requires the minimum date as
   * as a JS date.
   */
  get maxDateAsDate(): Date | undefined {
    return createDateFromDateLike(this.maxDate, this.format);
  }

  // get/set inputs
  public _noLabel = false;
  @Input()
  get noLabel(): string | boolean {
    return this._noLabel;
  }

  set noLabel(value: string | boolean) {
    this._noLabel =
      typeof value === 'string' ? stringAsBooleanProp(value) : value;
  }

  public _required = false;
  @Input()
  get required(): string | boolean {
    return this._required;
  }

  set required(value: string | boolean) {
    this._required =
      typeof value === 'string' ? stringAsBooleanProp(value) : value;
  }

  // calculated/set on init
  id: string;

  ngOnInit(): void {
    const groupText =
      typeof this.group === 'number' ? this.group.toFixed(0) : this.group;
    this.id = groupText !== null ? `${groupText}-${this.name}` : this.name;
  }

  onDateSelected(isoDate: NullableString): void {
    if (!isoDate) {
      return;
    }
    const datePartOnly = DateTime.fromISO(isoDate).toFormat(this.format);
    // emit value for template forms
    this.valueChange.emit(datePartOnly);
    // set control value for reactive forms
    this.formControl?.setValue(datePartOnly);
  }

  onDateRangeSelected(dates: (Date | undefined)[] | undefined): void {
      if (dates && dates?.filter(date => date !== undefined).length === 2) {
        this.rangeValueChange.emit(dates);
      }
  }
}
